import type {
  ProductStructureInput,
  ProductionOrderFormValues,
  ProductionOrderView,
  ProductionRunView,
  StructureItemView,
} from "@cx/contracts";
import { MAX_LOSS_PCT4, componentRequirementQ4, productionCostCents } from "@cx/domain";
import { emptyLotDraft, lotDraftProblem, lotPayload, type LotDraft } from "../stock/labels";

// ===== Estrutura de produção e ordens de produção: estado das telas e rótulos (T3.2, RF-SUP-05) =====

/** Insumo na tela (estrutura do produto ou insumos da OP): quantidade por 1 unidade do acabado e perda. */
export type StructureRow = {
  componentId: string;
  name: string;
  sku: string | null;
  unit: string;
  qtyQ4: number | null;
  lossPct4: number | null;
  /** Custo médio (só com products:read_cost). */
  avgCostCents?: number;
};

export const rowsFromStructure = (items: readonly StructureItemView[]): StructureRow[] =>
  items.map((i) => ({
    componentId: i.componentId,
    name: i.name,
    sku: i.sku,
    unit: i.unit,
    qtyQ4: i.qtyQ4,
    lossPct4: i.lossPct4,
    ...(i.avgCostCents !== undefined ? { avgCostCents: i.avgCostCents } : {}),
  }));

/** Necessário de um insumo para `qtyQ4` do acabado (com perda), ou null se a conta não fecha na tela. */
export function requiredQ4(
  perUnitQ4: number | null,
  lossPct4: number | null,
  qtyQ4: number | null,
): number | null {
  if (!perUnitQ4 || !qtyQ4) return null;
  try {
    return componentRequirementQ4(perUnitQ4, lossPct4 ?? 0, qtyQ4, { allowZero: true });
  } catch {
    return null;
  }
}

/** Custo dos insumos por 1 unidade do acabado; null se algum insumo vem sem custo (sem products:read_cost). */
export function structureCostCents(rows: readonly StructureRow[]): number | null {
  if (rows.some((r) => r.avgCostCents === undefined)) return null;
  return productionCostCents(
    rows.map((r) => ({
      qtyQ4: requiredQ4(r.qtyQ4, r.lossPct4, 10_000) ?? 0,
      unitCostCents: r.avgCostCents!,
    })),
  );
}

/** Problema da lista de insumos na tela (o servidor confere de novo, inclusive ciclo). */
export function structureProblem(rows: readonly StructureRow[]): string | null {
  for (const r of rows) {
    if (!r.qtyQ4 || r.qtyQ4 <= 0) return `${r.name}: informe a quantidade por unidade.`;
    const loss = r.lossPct4 ?? 0;
    if (loss < 0 || loss > MAX_LOSS_PCT4) return `${r.name}: a perda vai de 0% a 99,9999%.`;
  }
  return null;
}

export const structurePayload = (rows: readonly StructureRow[]): ProductStructureInput => ({
  items: rows.map((r) => ({ componentId: r.componentId, qtyQ4: r.qtyQ4 ?? 0, lossPct4: r.lossPct4 ?? 0 })),
});

/** Produto acabado escolhido no formulário da OP. */
export type ProductionProduct = { id: string; name: string; unit: string; batchControl: boolean };

export type ProductionForm = {
  product: ProductionProduct | null;
  quantityQ4: number | null;
  sourceWarehouseId: string;
  targetWarehouseId: string;
  responsible: string;
  plannedStart: string;
  plannedEnd: string;
  notes: string;
  lot: LotDraft;
  items: StructureRow[];
};

export const emptyProductionForm = (): ProductionForm => ({
  product: null,
  quantityQ4: null,
  sourceWarehouseId: "",
  targetWarehouseId: "",
  responsible: "",
  plannedStart: "",
  plannedEnd: "",
  notes: "",
  lot: emptyLotDraft(),
  items: [],
});

export function productionFormFromView(o: ProductionOrderView): ProductionForm {
  return {
    product: {
      id: o.product.id,
      name: o.product.name,
      unit: o.product.unit,
      batchControl: o.product.batchControl,
    },
    quantityQ4: o.quantityQ4,
    sourceWarehouseId: o.sourceWarehouse.id,
    targetWarehouseId: o.targetWarehouse.id,
    responsible: o.responsible ?? "",
    plannedStart: o.plannedStart ?? "",
    plannedEnd: o.plannedEnd ?? "",
    notes: o.notes ?? "",
    lot: o.lot
      ? { code: o.lot.code, manufacturedAt: o.lot.manufacturedAt ?? "", expiresAt: o.lot.expiresAt ?? "" }
      : emptyLotDraft(),
    items: o.items.map((i) => ({
      componentId: i.componentId,
      name: i.name,
      sku: i.sku,
      unit: i.unit,
      qtyQ4: i.qtyPerUnitQ4,
      lossPct4: i.lossPct4,
    })),
  };
}

export function productionFormProblem(f: ProductionForm): string | null {
  if (!f.product) return "Escolha o produto a produzir.";
  if (!f.quantityQ4 || f.quantityQ4 <= 0) return "Informe a quantidade a produzir.";
  if (f.items.length === 0) return "Inclua ao menos um insumo (cadastre a estrutura do produto).";
  const items = structureProblem(f.items);
  if (items) return items;
  if (f.plannedStart && f.plannedEnd && f.plannedEnd < f.plannedStart)
    return "A previsão de término deve ser igual ou posterior ao início.";
  return f.product.batchControl ? lotDraftProblem(f.lot, false) : null;
}

const orNull = (s: string) => s.trim() || null;

export function productionPayload(f: ProductionForm): ProductionOrderFormValues {
  return {
    productId: f.product!.id,
    quantityQ4: f.quantityQ4!,
    sourceWarehouseId: f.sourceWarehouseId || null,
    targetWarehouseId: f.targetWarehouseId || null,
    responsible: orNull(f.responsible),
    plannedStart: f.plannedStart || null,
    plannedEnd: f.plannedEnd || null,
    notes: orNull(f.notes),
    lot: f.product!.batchControl ? lotPayload(f.lot) : null,
    items: f.items.map((r) => ({
      componentId: r.componentId,
      qtyPerUnitQ4: r.qtyQ4 ?? 0,
      lossPct4: r.lossPct4 ?? 0,
    })),
  };
}

export const PRODUCTION_PROGRESS_LABELS: Readonly<Record<ProductionOrderView["progress"], string>> = {
  none: "Nada produzido",
  partial: "Produzida em parte",
  complete: "Produzida",
};

export const RUN_SOURCE_LABELS: Readonly<Record<ProductionRunView["source"], string>> = {
  manual: "Apontamento",
  finish: "Finalização",
  status: "Mudança de situação",
};
